import { keyframes } from '@emotion/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ClaimStepper } from '@/core/components';
import { useRace } from '@/core/contexts/RaceContext';
import { useAuth } from '@/core/hooks/useAuth';
import { getRunnerByTimingHash } from '@/core/services/claim';
import type { Runner } from '@/core/types';
import { OnSiteRunnerConfirmModal } from './components';
import { LoginButtons } from './LoginButtons';

// アニメーション定義
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 左下からの花吹雪
const burstFromLeft = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) rotate(var(--rot));
    opacity: 0;
  }
`;

// 右下からの花吹雪
const burstFromRight = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) rotate(var(--rot));
    opacity: 0;
  }
`;

// 紙吹雪の色（カラフルに）
const confettiColors = [
  '#FF8800', // オレンジ（ブランド）
  '#6A2BFF', // パープル（ブランド）
  '#FFD700', // ゴールド
  '#FF4081', // ピンク
  '#00BCD4', // シアン
  '#4CAF50', // グリーン
  '#FF5722', // ディープオレンジ
  '#E91E63', // ローズ
  '#9C27B0', // パープル
  '#FFEB3B', // イエロー
  '#03A9F4', // ライトブルー
  '#8BC34A', // ライトグリーン
];

// 紙吹雪の生成パラメータ
const generateConfetti = (side: 'left' | 'right', count: number) => {
  return Array.from({ length: count }, (_, i) => {
    // 角度: 左は右上方向、右は左上方向に広がる
    const baseAngle = side === 'left' ? 60 : 120; // 左:60度、右:120度を基準
    const spread = 50; // 広がり角度
    const angle = baseAngle + (Math.random() - 0.5) * spread;

    // 距離: もっと遠くまで飛ばす
    const distance = 400 + Math.random() * 500;

    // 移動量計算
    const rad = (angle * Math.PI) / 180;
    const tx = Math.cos(rad) * distance;
    const ty = -Math.sin(rad) * distance;

    const rotation = (Math.random() - 0.5) * 1080; // もっと回転
    const delay = Math.random() * 0.2; // 短いディレイでバシューン感
    const duration = 1.2 + Math.random() * 0.8; // 少し速く
    const size = 6 + Math.random() * 10; // 少し大きく
    const color = confettiColors[Math.floor(Math.random() * confettiColors.length)];

    return { tx, ty, rotation, delay, duration, size, color, id: `${side}-${i}` };
  });
};

export function LoginPage() {
  const { raceAlias } = useParams<{ raceAlias: string }>();
  const { race } = useRace();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const theme = useTheme();

  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  // オンサイト確認モーダル用の状態
  const [onSiteRunner, setOnSiteRunner] = useState<Runner | null>(null);
  const [showOnSiteModal, setShowOnSiteModal] = useState(false);
  const [isLoadingOnSite, setIsLoadingOnSite] = useState(false);

  // sessionStorage の login_return_url から code を取得してランナー情報を取得
  useEffect(() => {
    const fetchOnSiteRunner = async () => {
      const returnUrl = sessionStorage.getItem('login_return_url');
      if (!returnUrl) return;

      try {
        const url = new URL(returnUrl, window.location.origin);
        const code = url.searchParams.get('code');
        if (!code) return;

        // codeがあれば先にモーダルを表示してからデータ取得
        setShowOnSiteModal(true);
        setIsLoadingOnSite(true);

        const runner = await getRunnerByTimingHash(code);
        if (runner) {
          setOnSiteRunner(runner);
        }
      } catch (err) {
        console.error('Failed to fetch on-site runner:', err);
      } finally {
        setIsLoadingOnSite(false);
      }
    };

    fetchOnSiteRunner();
  }, []);

  // 紙吹雪を生成（初回のみ）
  const leftConfetti = useMemo(() => generateConfetti('left', 80), []);
  const rightConfetti = useMemo(() => generateConfetti('right', 80), []);

  // ログイン後のリダイレクト先（sessionStorage に code 付きで保存されていればそれを使う）
  const storedReturnUrl = sessionStorage.getItem('login_return_url');
  const returnUrl = storedReturnUrl || `/${raceAlias}/claim`;

  // ログイン済みなら claim ページへリダイレクト
  if (!authLoading && user) {
    return <Navigate to={`/${raceAlias}/claim`} replace />;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* 背景の装飾要素 - モーダルより前面に表示 */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          zIndex: 1400,
        }}
      >
        {/* 紙吹雪 - 左下から */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
          }}
        >
          {leftConfetti.map((c) => (
            <Box
              key={c.id}
              sx={{
                position: 'absolute',
                width: c.size,
                height: c.size * 0.6,
                backgroundColor: c.color,
                borderRadius: '1px',
                '--tx': `${c.tx}px`,
                '--ty': `${c.ty}px`,
                '--rot': `${c.rotation}deg`,
                animation: `${burstFromLeft} ${c.duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${c.delay}s forwards`,
              }}
            />
          ))}
        </Box>

        {/* 紙吹雪 - 右下から */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
          }}
        >
          {rightConfetti.map((c) => (
            <Box
              key={c.id}
              sx={{
                position: 'absolute',
                width: c.size,
                height: c.size * 0.6,
                backgroundColor: c.color,
                borderRadius: '1px',
                '--tx': `${c.tx}px`,
                '--ty': `${c.ty}px`,
                '--rot': `${c.rotation}deg`,
                animation: `${burstFromRight} ${c.duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${c.delay}s forwards`,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* メインコンテンツ */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* ステッパー - 上下均等（モバイルは狭め） */}
        <Box
          sx={{
            mt: { xs: 2, sm: 5 },
            mb: { xs: 2, sm: 5 },
            animation: `${fadeInUp} 0.6s ease-out`,
          }}
        >
          <ClaimStepper activeStep={0} />
        </Box>

        {/* タイトルエリア - 上下に余裕を持たせる */}
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 3, sm: 5 },
            animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
          }}
        >
          {/* 達成を祝うサブテキスト */}
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              letterSpacing: '0.2em',
              color: 'text.secondary',
              mb: 1,
              fontSize: '0.7rem',
            }}
          >
            FINISHER'S VIDEO CERTIFICATE
          </Typography>

          {/* タイトル - 大会名 + デジタル完走証 */}
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 800,
              background: gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              lineHeight: 1.4,
            }}
          >
            {race.name}
            <br />
            デジタル完走証
          </Typography>

          {/* 説明テキスト */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              maxWidth: '300px',
              mx: 'auto',
            }}
          >
            あなたの挑戦の道のりを再現した
            <br />
            20秒の動画をお届けします
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              animation: `${fadeInUp} 0.6s ease-out`,
            }}
          >
            {error}
          </Alert>
        )}

        {/* ログインボタン */}
        <Box
          sx={{
            animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
          }}
        >
          <LoginButtons returnUrl={returnUrl} />
        </Box>

        {/* 利用規約 */}
        <Box
          sx={{
            mt: 4,
            animation: `${fadeInUp} 0.6s ease-out 0.3s both`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.disabled',
              lineHeight: 1.6,
            }}
          >
            ログインすることで、
            <Box
              component="a"
              href="https://2dot99.com/tos.html"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'text.secondary',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' },
              }}
            >
              利用規約
            </Box>
            と
            <Box
              component="a"
              href="https://2dot99.com/privacypolicy.html"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'text.secondary',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' },
              }}
            >
              プライバシーポリシー
            </Box>
            に
            <br />
            同意したものとみなされます。
          </Typography>
        </Box>
      </Box>

      {/* オンサイト確認モーダル */}
      <OnSiteRunnerConfirmModal
        runner={onSiteRunner}
        open={showOnSiteModal}
        loading={isLoadingOnSite}
        onClose={() => setShowOnSiteModal(false)}
      />
    </Box>
  );
}
